import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import {
  Auth,
  GoogleAuthProvider,
  User,
  createUserWithEmailAndPassword,
  getIdTokenResult,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  Unsubscribe,
} from '@angular/fire/auth';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  readonly #auth = inject(Auth);
  readonly #unsubscribe: Unsubscribe;

  readonly currentUser = signal<User | null>(null);
  readonly isAuthenticated = computed(() => this.currentUser() !== null);
  readonly isLoading = signal(true);
  readonly #claims = signal<Record<string, unknown>>({});
  readonly isAdmin = computed(() => this.#claims()['role'] === 'admin');

  constructor() {
    let autoLoginAttempted = false;

    this.#unsubscribe = onAuthStateChanged(this.#auth, async (user) => {
      this.currentUser.set(user);

      if (user) {
        try {
          const tokenResult = await getIdTokenResult(user);
          this.#claims.set(tokenResult.claims);
        } catch {
          this.#claims.set({});
        }
        this.isLoading.set(false);
      } else {
        this.#claims.set({});

        if (!autoLoginAttempted && environment.useEmulators && environment.autoLogin) {
          autoLoginAttempted = true;
          // Keep isLoading true while auto-login is in progress so guards wait
          await this.#autoLogin(environment.autoLogin.email, environment.autoLogin.password);
          // onAuthStateChanged will fire again with the new user, setting isLoading there
        } else {
          this.isLoading.set(false);
        }
      }
    });
  }

  async #autoLogin(email: string, password: string): Promise<void> {
    try {
      await signInWithEmailAndPassword(this.#auth, email, password);
    } catch (signInError: unknown) {
      const code = (signInError as { code?: string })?.code;
      if (code === 'auth/user-not-found' || code === 'auth/invalid-credential') {
        try {
          await createUserWithEmailAndPassword(this.#auth, email, password);
        } catch (createError) {
          console.error('Auto-login user creation failed:', createError);
        }
      } else {
        console.error('Auto-login sign-in failed:', signInError);
      }
    }
  }

  ngOnDestroy() {
    this.#unsubscribe();
  }

  async signInWithGoogle(): Promise<void> {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(this.#auth, provider);
  }

  async signOut(): Promise<void> {
    await signOut(this.#auth);
  }

  async getIdToken(): Promise<string | null> {
    const user = this.currentUser();
    if (!user) return null;
    return user.getIdToken();
  }

  async forceTokenRefresh(): Promise<void> {
    const user = this.currentUser();
    if (!user) return;
    const tokenResult = await getIdTokenResult(user, true);
    this.#claims.set(tokenResult.claims);
  }
}
